import _ from 'lodash';
import Service from './abstract';
import accountsService from './accounts';

const withPersonSmall = 'person,person.attributes,person.attributes.attribute';
const withPersonEmail = 'person.emails,person.emails.type';
const withPersonDocuments = 'person.documents,person.documents.type';
const withPersonTelephones = 'person.telephones,person.telephones.type';
const withPersonAddress = 'person.addresses,person.addresses.state,person.addresses.country,person.addresses.city,person.addresses.type';
const withDevices = 'person.devices,person.devices.type';
const withPerson = `${withPersonSmall},${withDevices},${withPersonTelephones},${withPersonDocuments},${withPersonEmail}`;
const withAccount = 'accounts,accounts.account,accounts.account.attributes,accounts.account.attributes.attribute,accounts.account.wallet,accounts.account.currency,accounts.account.currency.attributes,accounts.account.currency.attributes.attribute,accounts.account.product,accounts.account.product.attributes,accounts.account.product.attributes.attribute,accounts.account.product.type';

const defaultWith = `roles,roles.role,status,${withPerson},${withAccount}`;
const fullWith = `${defaultWith},${withPersonAddress}`;
const fullWidthSmall = `${withPersonSmall},${withPersonDocuments}`;

const defaultOptionsFind = {
    order: 'created_at-DESC',
    with: defaultWith
};
const defaultOptionsGetById = {
    with: fullWith
};

const defaultOptionsFindSmall = {
    order: 'created_at-DESC',
    with: fullWidthSmall
};

const defaultEvents = {
    tiny: false
};

const defaultCards = {
    with: 'type,cardStatus,product,account'
};

const defaultAccountsSmall = {
    order: 'created_at-DESC',
    tiny: true,
    pageSize: 10
};
class Users extends Service {
    constructor () {
        super('users');
    }

    get personWithSmall () {
        return withPersonSmall;
    }

    get personWith () {
        return withPerson;
    }

    get fullWith () {
        return fullWith;
    }

    get defaultOptionsFind () {
        return _.merge({}, super.defaultOptionsFind, defaultOptionsFind);
    }

    get defaultOptionsGetById () {
        return _.merge({}, super.defaultOptionsGetById, defaultOptionsGetById);
    }

    findSmall (data = {}) {
        const opts = {
            url: `${this.basePath}/users`,
            qs: _.defaultsDeep({}, data, defaultOptionsFindSmall),
        };

        return this.get(opts).then(users => this.normalizeAll(users));
    }

    getAccountsSmall (id) {
        const opts = {
            url: `${this.basePath}/users/${id}/accounts`,
            qs: defaultAccountsSmall
        };

        return this.get(opts);
    }

    toggle (id, data = {}) {
        const opts = {
            // TODO fix base url. When cross domain resolve for PUT method
            url: `${this.basePath}/users/${id}/toggle`,
            data
        };

        return this.put(opts).then(user => user);
    }

    editById (id, data = {}) {
        const opts = {
            // TODO fix base url. When cross domain resolve for PUT method
            url: `${this.basePath}/users/${id}`,
            data
        };

        return this.put(opts).then(users => this.normalize(users));
    }

    editRole (id, data = {}) {
        const opts = {
            url: `${this.basePath}/users/${id}/roles`,
            data: data.toCreate
        };

        return this.delete({
            url: `${this.basePath}/users/${id}/roles/${data.toDelete}`
        }).then(res => {
            return this.post(opts);
        });
    }

    blackList (id, data = {}) {
        const opts = {
            url: `${this.basePath}/users/${id}/blacklist`,
            data
        };

        return super.put(opts).then(user => this.normalize(user));
    }

    onboardingOperation (id) {
        const opts = {
            url: `${this.basePath}/users/${id}/onboarding/operation`
        };

        return super.get(opts);
    }

    userEvents (id, data) {
        const opts = {
            url: `${this.basePath}/users/${id}/events`,
            qs: _.defaultsDeep({}, data, defaultEvents)
        };

        return super.get(opts);
    }

    usersCards (data = {}) {
        const opts = {
            url: `${this.basePath}/cards`,
            qs: _.defaultsDeep({}, data, defaultCards)
        };

        return super.get(opts);
    }

    normalize (user) {
        if (_.isNil(user)) {
            return user;
        }

        // Prepare default properties
        user = super.normalize(user);

        // Prepare person
        if (user.person) {
            // Prepare attributes
            if (user.person.attributes) {
                user.person.rawAttributes = user.person.attributes;
                user.person.attributes = _.keyBy(user.person.attributes, 'description');

                // Prepare formatted name.
                user.formattedName = '';
                if (user.person.attributes.first_name) {
                    user.formattedName = user.person.attributes.first_name.value;
                }
                if (user.person.attributes.last_name) {
                    user.formattedName = _.trim(`${user.formattedName} ${user.person.attributes.last_name.value}`);
                }
            }

            // Prepare documents
            if (!user.person.document && user.person.documents && user.person.documents.length) {
                user.person.document = _.find(user.person.documents, ['type.description', 'dni']) || user.person.documents[0];
                user.person.cuil = _.find(user.person.documents, ['type.description', 'cuil']) || '-';
                user.person.cuit = _.find(user.person.documents, ['type.description', 'cuit']) || '-';
            }

            // Prepare telephones
            if (!user.person.telephone && user.person.telephones && user.person.telephones.length) {
                user.person.telephone = _.find(user.person.telephones, ['type.description', 'mobile']) || user.person.telephones[0];
            }

            // Prepare emails
            if (!user.person.email && user.person.emails && user.person.emails.length) {
                user.person.email = _.find(user.person.emails, ['type.description', 'home']) || user.person.emails[0];
            }
        }

        // Prepare accounts
        if (user.accounts) {
            user.accounts = _.map(user.accounts, account => {
                account.account = accountsService.normalize(account.account);
                return account;
            });

            // Main finket account
            if (user.accounts.length) {
                user.mainAccount = user.accounts[0].account;
            }
        }

        return user;
    }

    getPersonByDocument (document) {
        return this.get({
            url: `${this.basePath}/people/${document}`,
        });
    }
}

export default new Users();
